import React, { useEffect, useState, useCallback } from 'react';
import PageHeader from '../../components/PageHeader';
import { toast } from 'sonner';
import api, { apiError } from '../../lib/api';
import { DEMO } from '../../lib/demo';
import { FileText, Loader2, RefreshCw, Trophy, TrendingUp, TrendingDown, ChevronDown, ChevronUp, Eye, EyeOff } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

const fmtEur = (n) => `${(n || 0).toLocaleString('fr-FR')} €`;
const fmtResp = (s) => (s == null ? '—' : s < 60 ? `${s}s` : s < 3600 ? `${Math.round(s / 60)}min` : `${Math.round(s / 3600)}h`);
const weekLabel = (iso) => {
  try {
    const d = new Date(iso + 'T00:00:00');
    const e = new Date(d); e.setDate(d.getDate() + 6);
    const f = (x) => x.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });
    return `${f(d)} → ${f(e)}`;
  } catch { return iso; }
};
const delta = (cur, prev) => {
  if (prev == null || cur == null) return null;
  if (prev === 0) return cur > 0 ? 100 : 0;
  return Math.round(((cur - prev) / prev) * 100);
};

const DEMO_DATA = {
  week_start: '2026-08-17', weeks: ['2026-08-17'],
  reports: [{
    chatter_id: 'ch1', chatter_name: 'Lina', is_top: true, top_name: 'Lina',
    facts: { ca: 1840, ventes: 24, ppv: 61, ppv_vendus: 20, taux_ppv: 32.8, panier: 76.7, messages: 2100, convs: 48, jours_actifs: 6, reponse_moy_s: 240 },
    prev_facts: { ca: 1500, ventes: 20 },
    result: { resume: 'Grosse semaine, en progression.', ce_qui_a_change: ['+340 € vs la semaine dernière'], ecart_avec_le_top: [], erreurs: [], actions: ['Continue comme ça'] },
  }],
};

function Kpi({ label, value, d }) {
  return (
    <div className="rounded-lg bg-white/[0.04] px-3 py-2">
      <div className="text-[10px] text-slate-500">{label}</div>
      <div className="text-sm font-semibold text-white flex items-center gap-1.5">
        {value}
        {d != null && d !== 0 && (
          <span className={`text-[10px] flex items-center ${d > 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
            {d > 0 ? <TrendingUp size={10} /> : <TrendingDown size={10} />}{d > 0 ? '+' : ''}{d}%
          </span>
        )}
      </div>
    </div>
  );
}

// Fil de conversation RÉEL (extrait) — bulles fan à gauche / chatteur à droite.
function ConvExtract({ e, tone }) {
  if (!e || !(e.messages || []).length) return null;
  const border = tone === 'red' ? 'border-rose-500/25' : 'border-emerald-500/25';
  return (
    <div className={`rounded-xl border ${border} bg-black/20 p-3 space-y-1.5 max-h-[340px] overflow-y-auto scrollbar-thin`}>
      <div className="text-[10px] text-slate-500 mb-1">Conversation réelle avec « {e.fan} »</div>
      {e.contexte?.signal_fauche && (
        <div className="text-[10px] text-amber-300 bg-amber-500/10 border border-amber-400/25 rounded-lg px-2 py-1">
          ⚠️ Ce fan avait dit ne pas pouvoir payer — {e.contexte.signal_fauche}
        </div>
      )}
      {e.contexte?.tags?.length > 0 && (
        <div className="text-[10px] text-slate-400">🏷️ {e.contexte.tags.join(' · ')}</div>
      )}
      {e.messages.map((m, i) => (
        <div key={i} className={`flex ${m.de === 'fan' ? 'justify-start' : 'justify-end'}`}>
          <div className={`max-w-[85%] rounded-xl px-2.5 py-1.5 text-[12px] leading-snug ${m.de === 'fan' ? 'bg-white/[0.07] text-slate-200' : m.lien ? 'bg-amber-500/15 text-amber-100 border border-amber-400/30' : 'bg-indigo-500/20 text-indigo-50'}`}>
            {m.lien && <span className="text-[10px] font-bold text-amber-300 block">💳 LIEN DE PAIEMENT</span>}
            {m.script && <span className="text-[9px] text-slate-400 block">script</span>}
            <span className="whitespace-pre-wrap break-words">{m.texte}</span>
            <span className="block text-[9px] opacity-50 mt-0.5">{m.heure}</span>
          </div>
        </div>
      ))}
    </div>
  );
}

// Rendu complet d'UN rapport (réutilisé par l'espace chatteur via export).
export function ReportBody({ r, showTop = true }) {
  const A = r.result || {};
  const f = r.facts || {};
  const p = r.prev_facts || {};
  const disc = r.discipline || {};
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2">
        <Kpi label="CA" value={fmtEur(f.ca)} d={delta(f.ca, p.ca)} />
        <Kpi label="Ventes" value={f.ventes ?? 0} d={delta(f.ventes, p.ventes)} />
        <Kpi label="PPV envoyés" value={f.ppv ?? 0} d={delta(f.ppv, p.ppv)} />
        <Kpi label="PPV vendus" value={`${f.ppv_vendus ?? 0}/${f.ppv ?? 0}`} />
        <Kpi label="Panier moyen" value={fmtEur(f.panier)} />
        <Kpi label="Messages" value={f.messages ?? 0} d={delta(f.messages, p.messages)} />
        <Kpi label="% aux nouveaux fans" value={f.pct_msgs_nouveaux != null ? `${f.pct_msgs_nouveaux}%` : '—'} />
        <Kpi label="Réponse médiane (en ligne)" value={fmtResp(f.reponse_med_s ?? f.reponse_moy_s)} />
      </div>
      {A.resume && <div className="text-sm text-slate-200 bg-white/[0.03] border border-white/10 rounded-xl p-3">{A.resume}</div>}
      <div className="grid md:grid-cols-2 gap-3">
        {(A.ce_qui_a_change || []).length > 0 && (
          <div className="rounded-xl border border-white/10 bg-white/[0.02] p-3">
            <div className="text-[12px] font-semibold text-sky-300 mb-2">📈 Ce qui a changé vs la semaine d'avant</div>
            <ul className="space-y-1.5">{A.ce_qui_a_change.map((x, i) => <li key={i} className="text-[12px] text-slate-300 flex gap-2"><span className="text-sky-400">•</span>{x}</li>)}</ul>
          </div>
        )}
        {(A.ecart_avec_le_top || []).length > 0 && (
          <div className="rounded-xl border border-white/10 bg-white/[0.02] p-3">
            <div className="text-[12px] font-semibold text-amber-300 mb-2">🏆 Ce que le meilleur fait de différent</div>
            <ul className="space-y-1.5">{A.ecart_avec_le_top.map((x, i) => <li key={i} className="text-[12px] text-slate-300 flex gap-2"><span className="text-amber-400">•</span>{x}</li>)}</ul>
          </div>
        )}
      </div>
      {(r.progres || []).length > 0 && (
        <div className="rounded-xl border border-sky-500/20 bg-sky-500/[0.04] p-3">
          <div className="text-[12px] font-semibold text-sky-300 mb-2">📈 Progrès vs semaine dernière</div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-1.5">
            {r.progres.map((x, i) => (
              <div key={i} className="text-[11px] bg-black/20 rounded-lg px-2.5 py-1.5 flex items-center justify-between gap-2">
                <span className="text-slate-300">{x.label}</span>
                <span className={`font-semibold shrink-0 ${x.sens === 'mieux' ? 'text-emerald-300' : x.sens === 'moins bien' ? 'text-rose-300' : 'text-slate-400'}`}>
                  {x.avant}{x.unite} → {x.maintenant}{x.unite} {x.sens === 'mieux' ? '✅' : x.sens === 'moins bien' ? '⚠️' : '＝'}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
      {r.extrait_perdu && (
        <div>
          <div className="text-[12px] font-semibold text-rose-300 mb-2">🔴 Là, la vente s'est perdue — conversation réelle</div>
          <div className="grid lg:grid-cols-2 gap-3">
            <ConvExtract e={r.extrait_perdu} tone="red" />
            {A.analyse_perdu && (A.analyse_perdu.moment || A.analyse_perdu.explication) && (
              <div className="rounded-xl border border-white/10 bg-white/[0.02] p-3 space-y-2">
                {A.analyse_perdu.moment && <div className="text-[12px] text-rose-200"><span className="font-semibold">Le moment où ça a basculé :</span> {A.analyse_perdu.moment}</div>}
                {A.analyse_perdu.explication && <div className="text-[12px] text-slate-300">{A.analyse_perdu.explication}</div>}
              </div>
            )}
          </div>
        </div>
      )}
      {r.extrait_gagnant && (
        <div>
          <div className="text-[12px] font-semibold text-emerald-300 mb-2">🟢 Là, ça a payé — {r.gagnant_team ? "séquence gagnante de l'équipe (anonyme)" : 'sa conversation réelle'}</div>
          <div className="grid lg:grid-cols-2 gap-3">
            <ConvExtract e={r.extrait_gagnant} tone="green" />
            {A.analyse_gagnant && <div className="rounded-xl border border-white/10 bg-white/[0.02] p-3 text-[12px] text-slate-300">{A.analyse_gagnant}</div>}
          </div>
        </div>
      )}
      {(disc.cas || []).length > 0 && (
        <div className="rounded-xl border border-amber-500/20 bg-amber-500/[0.04] p-3">
          <div className="text-[12px] font-semibold text-amber-300 mb-2">📋 Discipline — {disc.nb_fans_fauches} fan(s) ont dit ne pas pouvoir payer</div>
          <div className="flex items-center gap-2 flex-wrap mb-2 text-[11px]">
            <span className={`px-2 py-0.5 rounded-full ${disc.liens_apres_refus ? 'bg-rose-500/15 text-rose-300' : 'bg-emerald-500/15 text-emerald-300'}`}>{disc.liens_apres_refus || 0} lien(s) envoyé(s) APRÈS le refus</span>
            <span className={`px-2 py-0.5 rounded-full ${disc.sans_note ? 'bg-rose-500/15 text-rose-300' : 'bg-emerald-500/15 text-emerald-300'}`}>{disc.sans_note || 0} sans note posée</span>
            <span className={`px-2 py-0.5 rounded-full ${disc.sans_tag ? 'bg-rose-500/15 text-rose-300' : 'bg-emerald-500/15 text-emerald-300'}`}>{disc.sans_tag || 0} sans tag</span>
            {disc.sans_question != null && <span className={`px-2 py-0.5 rounded-full ${disc.sans_question ? 'bg-rose-500/15 text-rose-300' : 'bg-emerald-500/15 text-emerald-300'}`}>{disc.sans_question || 0} sans demander quand</span>}
          </div>
          <div className="space-y-1.5">
            {disc.cas.map((c, i) => (
              <div key={i} className="text-[11px] text-slate-300 bg-black/20 rounded-lg p-2">
                <span className="text-white">{c.fan}</span> ({c.quand}) : « {c.signal} » — liens après : <b className={c.liens_envoyes_apres ? 'text-rose-300' : 'text-emerald-300'}>{c.liens_envoyes_apres}</b> · note : <b className={c.note_posee ? 'text-emerald-300' : 'text-rose-300'}>{c.note_posee ? 'oui' : 'non'}</b> · tags : {(c.tags || []).join(', ') || <b className="text-rose-300">aucun</b>}{c.a_demande_quand != null && <> · a demandé quand/pourquoi : <b className={c.a_demande_quand ? 'text-emerald-300' : 'text-rose-300'}>{c.a_demande_quand ? 'oui' : 'NON'}</b></>}
              </div>
            ))}
          </div>
          {(A.discipline_constat || []).length > 0 && (
            <ul className="mt-2 space-y-1">{A.discipline_constat.map((x, i) => <li key={i} className="text-[11px] text-amber-100/80 flex gap-2"><span>•</span>{x}</li>)}</ul>
          )}
        </div>
      )}
      {/* Rétro-compat : anciens rapports (avant v3) avec erreurs/actions générées. */}
      {(A.erreurs || []).length > 0 && (
        <div>
          <div className="text-[12px] font-semibold text-white mb-2">🔧 À corriger — avant / après</div>
          <div className="grid md:grid-cols-2 gap-2">
            {A.erreurs.map((x, i) => (
              <div key={i} className="rounded-lg border border-white/10 bg-black/20 p-2.5">
                <div className="text-[12px] text-white mb-1">{x.probleme}</div>
                {x.pourquoi && <div className="text-[11px] text-slate-400 mb-1.5">{x.pourquoi}</div>}
                {x.au_lieu_de && <div className="text-[11px] text-rose-300/90">❌ {x.au_lieu_de}</div>}
                {x.dis_plutot && <div className="text-[11px] text-emerald-300">✅ {x.dis_plutot}</div>}
              </div>
            ))}
          </div>
        </div>
      )}
      {(A.actions || []).length > 0 && (
        <div className="rounded-xl border border-indigo-500/20 bg-indigo-500/[0.05] p-3">
          <div className="text-[12px] font-semibold text-indigo-300 mb-2">🎯 Actions pour la semaine prochaine</div>
          <ul className="space-y-1.5">{A.actions.map((x, i) => <li key={i} className="text-[12px] text-slate-200 flex gap-2"><span className="text-indigo-400 font-bold">{i + 1}.</span>{x}</li>)}</ul>
        </div>
      )}
      {!A.resume && !r.day && <div className="text-[12px] text-amber-200/80">Analyse IA indisponible pour ce rapport (chiffres seuls).</div>}
      {showTop && r.is_top && <div className="text-[11px] text-amber-300">🏆 Top vendeur de la semaine</div>}
    </div>
  );
}

export default function WeeklyReports() {
  const { hasPerm } = useAuth();
  const canToggle = typeof hasPerm === 'function' && hasPerm('manage_telegram');
  const [data, setData] = useState(DEMO ? DEMO_DATA : null);
  const [week, setWeek] = useState('');
  const [loading, setLoading] = useState(!DEMO);
  const [generating, setGenerating] = useState(false);
  const [open, setOpen] = useState(null);
  const [vis, setVis] = useState(false);
  const [mode, setMode] = useState('week'); // 'week' (bilan de fond) | 'day' (mini factuel de la veille)

  const load = useCallback(async (w, m = mode) => {
    if (DEMO) return;
    setLoading(true);
    try {
      if (m === 'day') {
        const { data } = await api.get('/reports/daily-all', { params: w ? { day: w } : {} });
        setData({ week_start: data.day, weeks: data.days, reports: data.reports, team: null, visible_to_chatters: vis });
        setWeek(data.day);
      } else {
        const { data } = await api.get('/reports/weekly-all', { params: w ? { week: w } : {} });
        setData(data); setWeek(data.week_start); setVis(!!data.visible_to_chatters);
      }
    } catch (e) { toast.error(apiError(e, 'Échec du chargement des rapports')); }
    finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);
  useEffect(() => { load(); }, [load]);

  const toggleVis = async () => {
    const next = !vis; setVis(next);
    try { await api.post('/reports/chatter-analysis-visibility', { visible: next }); }
    catch { setVis(!next); toast.error('Impossible de changer la visibilité'); }
  };

  const generate = async () => {
    setGenerating(true);
    toast.info(mode === 'day' ? 'Génération du mini-rapport du jour…' : 'Génération des rapports en cours… (~1 min, analyse IA par chatteur)');
    try {
      const ep = mode === 'day' ? '/reports/daily-generate' : '/reports/weekly-generate';
      const params = mode === 'day' ? (week ? { day: week } : {}) : (week ? { week } : {});
      const { data: g } = await api.post(ep, null, { params, timeout: 300000 });
      toast.success(`${g.reports} rapport(s) généré(s)`);
      load(week);
    } catch (e) { toast.error(apiError(e, 'Échec de la génération')); }
    finally { setGenerating(false); }
  };

  const reports = data?.reports || [];
  const weeks = data?.weeks?.length ? data.weeks : (week ? [week] : []);
  const top = reports.find((r) => r.is_top);

  return (
    <div className="p-6 md:p-8 max-w-7xl mx-auto fade-in" data-testid="weekly-reports-page">
      <PageHeader title="Rapports hebdo" subtitle="Bilan automatique par chatteur — chiffres, comparaison au top, conseils IA" onRefresh={() => load(week)} />

      <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-3 mb-4 flex items-center gap-3 flex-wrap">
        <div className="flex gap-1 p-1 rounded-lg bg-white/5 border border-white/10">
          {[['week', 'Hebdo'], ['day', 'Journalier']].map(([m, label]) => (
            <button key={m} onClick={() => { setMode(m); setWeek(''); }}
              className={`px-3 h-7 rounded-md text-xs font-medium transition-colors ${mode === m ? 'bg-white/10 text-white' : 'text-slate-400 hover:text-white'}`}>{label}</button>
          ))}
        </div>
        <select value={week} onChange={(e) => { setWeek(e.target.value); load(e.target.value); }}
          className="bg-white/5 border border-white/10 rounded-lg px-3 text-xs text-white h-9 outline-none">
          {weeks.map((w) => <option key={w} value={w} className="bg-[#0B0B0E]">{mode === 'day' ? `Journée du ${weekLabel(w).split(' → ')[0]}` : `Semaine du ${weekLabel(w)}`}</option>)}
          {!weeks.length && <option className="bg-[#0B0B0E]">Aucun rapport encore</option>}
        </select>
        <button onClick={generate} disabled={generating}
          className="flex items-center gap-1.5 px-3 h-9 rounded-lg text-xs text-indigo-200 bg-indigo-500/10 border border-indigo-400/30 hover:bg-indigo-500/20 disabled:opacity-50">
          {generating ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />} {generating ? 'Génération…' : 'Générer / actualiser'}
        </button>
        {canToggle && (
          <button onClick={toggleVis} title="Chaque chatteur voit alors SON rapport (top anonyme) dans Mes stats"
            className={`text-[12px] flex items-center gap-1.5 px-2.5 h-9 rounded-lg border transition-colors ${vis ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-300' : 'border-white/10 bg-white/[0.04] text-slate-400 hover:text-white'}`}>
            {vis ? <Eye size={13} /> : <EyeOff size={13} />} {vis ? 'Visible par les chatteurs' : 'Caché aux chatteurs'}
          </button>
        )}
        {top && <span className="ml-auto text-xs text-amber-300 flex items-center gap-1.5"><Trophy size={13} /> Top : <b>{top.chatter_name}</b> — {fmtEur(top.facts?.ca)}</span>}
        <span className="text-[11px] text-slate-500 w-full">Généré automatiquement chaque lundi matin. Côté chatteurs, la comparaison reste anonyme (« le meilleur de l'équipe »).</span>
      </div>

      {data?.team && (
        <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-4 mb-4">
          <div className="text-[12px] font-semibold text-white mb-3">🧭 Résumé d'équipe</div>
          <div className="flex items-center gap-4 flex-wrap text-[12px]">
            <span className="text-slate-300">CA équipe : <b className="text-emerald-400">{fmtEur(data.team.ca_total)}</b>
              {data.team.delta_pct != null && <span className={`ml-1 font-semibold ${data.team.delta_pct >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>({data.team.delta_pct > 0 ? '+' : ''}{data.team.delta_pct}% vs sem. préc.)</span>}
            </span>
            {data.team.monte && <span className="text-emerald-300">📈 Qui monte : <b>{data.team.monte.name}</b> (+{data.team.monte.delta_pct}%)</span>}
            {data.team.decroche && <span className="text-rose-300">📉 Qui décroche : <b>{data.team.decroche.name}</b> ({data.team.decroche.delta_pct}%)</span>}
          </div>
          <div className="flex items-center gap-2 flex-wrap mt-2 text-[11px]">
            <span className="px-2 py-0.5 rounded-full bg-white/[0.05] text-slate-300">{data.team.discipline?.fans_fauches || 0} fans « fauchés » détectés</span>
            <span className={`px-2 py-0.5 rounded-full ${data.team.discipline?.liens_apres_refus ? 'bg-rose-500/15 text-rose-300' : 'bg-emerald-500/15 text-emerald-300'}`}>{data.team.discipline?.liens_apres_refus || 0} liens après refus</span>
            <span className={`px-2 py-0.5 rounded-full ${data.team.discipline?.sans_note ? 'bg-amber-500/15 text-amber-300' : 'bg-emerald-500/15 text-emerald-300'}`}>{data.team.discipline?.sans_note || 0} sans note</span>
            <span className={`px-2 py-0.5 rounded-full ${data.team.discipline?.sans_question ? 'bg-amber-500/15 text-amber-300' : 'bg-emerald-500/15 text-emerald-300'}`}>{data.team.discipline?.sans_question || 0} sans demander quand</span>
            {data.team.erreur_commune && <span className="text-amber-200/80">⚠️ Erreur la plus répandue : <b>{data.team.erreur_commune}</b> ({data.team.erreur_commune_n} cas)</span>}
          </div>
        </div>
      )}
      {loading ? <div className="text-slate-400 text-sm flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> chargement…</div>
        : reports.length === 0 ? (
          <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-8 text-center text-slate-400 text-sm">
            Aucun rapport pour cette semaine. Clique « Générer / actualiser » pour les créer maintenant.
          </div>
        ) : (
          <div className="space-y-3">
            {reports.map((r) => {
              const f = r.facts || {}; const p = r.prev_facts || {};
              const d = delta(f.ca, p.ca);
              const isOpen = open === r.chatter_id;
              return (
                <div key={r.chatter_id} className={`rounded-2xl border ${r.is_top ? 'border-amber-400/30' : 'border-white/10'} bg-white/[0.02]`}>
                  <button onClick={() => setOpen(isOpen ? null : r.chatter_id)} className="w-full flex items-center gap-3 p-4 text-left">
                    <span className="text-sm font-semibold text-white flex items-center gap-2">
                      {r.is_top && <Trophy size={14} className="text-amber-400" />} {r.chatter_name}
                    </span>
                    <span className="text-sm text-emerald-400 font-bold">{fmtEur(f.ca)}</span>
                    {d != null && d !== 0 && (
                      <span className={`text-[11px] font-semibold ${d > 0 ? 'text-emerald-400' : 'text-rose-400'}`}>{d > 0 ? '+' : ''}{d}% vs sem. préc.</span>
                    )}
                    <span className="text-[11px] text-slate-500">{f.ventes ?? 0} ventes · {f.ppv ?? 0} PPV · {f.messages ?? 0} msgs</span>
                    <span className="ml-auto text-slate-500">{isOpen ? <ChevronUp size={15} /> : <ChevronDown size={15} />}</span>
                  </button>
                  {isOpen && <div className="px-4 pb-4"><ReportBody r={r} /></div>}
                </div>
              );
            })}
          </div>
        )}
    </div>
  );
}
